import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input } from '../components';
import { Flag, Clock, Lock, CheckCircle2, XCircle, FileText, AlertOctagon, Archive, Hourglass } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  fetchEigenMijlpalen,
  schrijfUrenOpMijlpaal,
  vraagUitbreidingAan,
  rondMijlpaalAf,
  type ProfessionalMijlpaal,
  type MijlpaalStatus,
} from '../services/projectenApi';

/**
 * Projectcockpit van de professional (stroom 3).
 *
 * Uren schrijven kan alleen op een mijlpaal die HR vooraf heeft vastgesteld,
 * en niet boven het budget. Zodra de urenstaat is ingediend gaat de mijlpaal
 * op slot tot HR heeft gefiatteerd of afgewezen — daarom toont dit scherm de
 * status prominent en verdwijnt het urenformulier zodra dat het geval is.
 */

const euro = (waarde: string | number | null | undefined): string => {
  const getal = typeof waarde === 'string' ? parseFloat(waarde) : waarde ?? 0;
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(getal || 0);
};

const STATUS_STIJL: Record<MijlpaalStatus, { klasse: string; icon: React.ComponentType<{ className?: string }> }> = {
  PLANNED: { klasse: 'bg-blue-100 text-blue-800', icon: Clock },
  SUBMITTED: { klasse: 'bg-yellow-100 text-yellow-800', icon: Lock },
  APPROVED: { klasse: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  REJECTED: { klasse: 'bg-red-100 text-red-800', icon: XCircle },
  INVOICED: { klasse: 'bg-gray-100 text-gray-800', icon: FileText },
  // Long-term B2B Project Engine & Budget-Grendel (136)
  LOCKED_PENDING: { klasse: 'bg-gray-100 text-gray-600', icon: Lock },
  ACTIVE_RUNNING: { klasse: 'bg-blue-100 text-blue-800', icon: Clock },
  CAP_REACHED_PENDING_APPROVAL: { klasse: 'bg-orange-100 text-orange-800', icon: AlertOctagon },
  COMPLETED_WAITING_FOR_FIAT: { klasse: 'bg-green-100 text-green-800', icon: Hourglass },
  ARCHIVED: { klasse: 'bg-gray-100 text-gray-800', icon: Archive },
};

/** Alleen op een openstaande mijlpaal mag geschreven worden. */
const magSchrijven = (status: MijlpaalStatus) => status === 'PLANNED' || status === 'REJECTED' || status === 'ACTIVE_RUNNING';
/** Wizard-mijlpalen (136): de professional rondt de fase zelf af → COMPLETED_WAITING_FOR_FIAT. */
const magAfronden = (m: ProfessionalMijlpaal) =>
  m.project_bron === 'WIZARD' && (m.status === 'ACTIVE_RUNNING' || m.status === 'CAP_REACHED_PENDING_APPROVAL') && parseFloat(m.geschreven_uren) > 0;
const fmtDatum = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('nl-NL') : '—');

const AfrondKnop: React.FC<{ mijlpaal: ProfessionalMijlpaal; onKlaar: () => void }> = ({ mijlpaal, onKlaar }) => {
  const { t } = useTranslation('professional');
  const mutation = useMutation({
    mutationFn: () => rondMijlpaalAf(mijlpaal.id),
    onSuccess: () => {
      toast.success(t('projecten.completed'));
      onKlaar();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('projecten.errorGeneric')),
  });
  return (
    <div className="mt-3">
      <Button
        size="sm"
        className="bg-green-600 hover:bg-green-700 border-green-600"
        loading={mutation.isPending}
        onClick={() => {
          if (window.confirm(t('projecten.completeConfirm', { uren: parseFloat(mijlpaal.geschreven_uren) }))) mutation.mutate();
        }}
      >
        <CheckCircle2 className="w-4 h-4 mr-1" /> {t('projecten.completeMilestone')}
      </Button>
    </div>
  );
};

const UrenFormulier: React.FC<{ mijlpaal: ProfessionalMijlpaal; onKlaar: () => void }> = ({ mijlpaal, onKlaar }) => {
  const { t } = useTranslation('professional');
  const [uren, setUren] = React.useState('');
  const [datum, setDatum] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [omschrijving, setOmschrijving] = React.useState('');

  const mutation = useMutation({
    mutationFn: () =>
      schrijfUrenOpMijlpaal(mijlpaal.id, {
        uren: parseFloat(uren),
        datum,
        omschrijving: omschrijving.trim(),
      }),
    onSuccess: () => {
      toast.success(t('projecten.hoursSaved'));
      setUren('');
      setOmschrijving('');
      onKlaar();
    },
    // De budget-grendel zit in de backend; die melding is specifieker dan
    // wat we hier kunnen verzinnen, dus tonen we hem letterlijk. Een 403
    // ("Cap exceeded") betekent dat de harde uren-cap dicht zit.
    onError: (err: any) =>
      toast.error(
        (err.response?.status === 403 ? `${t('projecten.capExceeded')}: ` : '') + (err.response?.data?.message || t('projecten.errorGeneric')),
        { duration: 7000 }
      ),
  });

  const geldig = parseFloat(uren) > 0 && omschrijving.trim().length >= 3 && Boolean(datum);

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Input
          type="number"
          step="0.25"
          min="0.25"
          label={t('projecten.hours')}
          value={uren}
          onChange={(e) => setUren(e.target.value)}
        />
        <Input type="date" label={t('projecten.date')} value={datum} onChange={(e) => setDatum(e.target.value)} />
        <Input
          label={t('projecten.description')}
          value={omschrijving}
          onChange={(e) => setOmschrijving(e.target.value)}
        />
      </div>
      <div className="mt-3">
        <Button size="sm" disabled={!geldig} loading={mutation.isPending} onClick={() => mutation.mutate()}>
          {t('projecten.saveHours')}
        </Button>
      </div>
    </div>
  );
};

/**
 * Stap 7: [📈 Vraag Urenuitbreiding Aan] — extra uren + harde functionele
 * onderbouwing. Zolang het verzoek op EXTENSION_PENDING staat, blokkeert de
 * backend elke ureninvoer boven het oorspronkelijke budget.
 */
const UitbreidingFormulier: React.FC<{ mijlpaal: ProfessionalMijlpaal; onKlaar: () => void }> = ({ mijlpaal, onKlaar }) => {
  const [open, setOpen] = React.useState(false);
  const [extraUren, setExtraUren] = React.useState('');
  const [onderbouwing, setOnderbouwing] = React.useState('');

  const mutation = useMutation({
    mutationFn: () => vraagUitbreidingAan(mijlpaal.id, parseFloat(extraUren), onderbouwing.trim()),
    onSuccess: () => {
      toast.success('Urenuitbreiding aangevraagd — HR ontvangt een kritiek signaal in het dashboard.');
      setOpen(false);
      setExtraUren('');
      setOnderbouwing('');
      onKlaar();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Aanvragen mislukt'),
  });

  if (!open) {
    return (
      <div className="mt-3">
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
          📈 Vraag Urenuitbreiding Aan
        </Button>
      </div>
    );
  }

  const geldig = parseFloat(extraUren) > 0 && onderbouwing.trim().length >= 20;

  return (
    <div className="mt-3 rounded-md border border-orange-200 bg-orange-50 p-4">
      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3">
        <Input type="number" step="0.5" min="0.5" label="Extra uren" value={extraUren}
          onChange={(e) => setExtraUren(e.target.value)} />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Harde functionele onderbouwing</label>
          <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm" rows={2}
            placeholder="Waarom is de situatie complexer dan vooraf ingeschat? (min. 20 tekens)"
            value={onderbouwing} onChange={(e) => setOnderbouwing(e.target.value)} />
        </div>
      </div>
      <div className="mt-2 flex gap-2">
        <Button size="sm" disabled={!geldig} loading={mutation.isPending} onClick={() => mutation.mutate()}>
          Verzoek indienen
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>Annuleren</Button>
      </div>
    </div>
  );
};

const ProfessionalProjectenPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['professional-mijlpalen'],
    queryFn: async () => (await fetchEigenMijlpalen()).data?.data as ProfessionalMijlpaal[],
  });

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-center text-gray-600 py-12">{t('projecten.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-center text-red-600 py-12">{t('projecten.loadError')}</p>
      </div>
    );
  }

  const mijlpalen = data || [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('projecten.title')}</h1>
      <p className="text-gray-600 mb-6">{t('projecten.subtitle')}</p>

      {mijlpalen.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Flag className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('projecten.empty')}</h2>
            <p className="text-gray-600">{t('projecten.emptyDesc')}</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {mijlpalen.map((m) => {
            const stijl = STATUS_STIJL[m.status];
            const StatusIcoon = stijl?.icon ?? Clock;
            const geschreven = parseFloat(m.geschreven_uren);
            const tarief = m.uurtarief ? parseFloat(m.uurtarief) : null;
            const budget = parseFloat(m.budget_bedrag);
            const isWizard = m.project_bron === 'WIZARD';
            const cap = isWizard && m.budget_uren ? parseFloat(m.budget_uren) : null;
            const extra = parseFloat(m.extra_uren_toegekend ?? '0') || 0;
            // Wizard-mijlpalen meten in uren (harde cap); OvO-mijlpalen in euro's.
            const benut = cap ? Math.min(geschreven / cap, 1) : tarief ? Math.min((geschreven * tarief) / budget, 1) : 0;

            return (
              <Card key={m.id}>
                <div className="p-6">
                  <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                    <div>
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <Flag className="w-5 h-5 text-purple-600" />
                        <span className="font-mono text-xs text-gray-600">{m.project_code}</span>
                        <h3 className="font-semibold text-gray-900">
                          {t('projecten.milestoneLabel', { nummer: m.volgnummer })}: {m.titel}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600">
                        {m.project_titel}
                        {m.werkgever_bedrijf && <span className="text-gray-400"> · {t('projecten.employer')}: {m.werkgever_bedrijf}</span>}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${stijl?.klasse}`}>
                      <StatusIcoon className="w-4 h-4" />
                      {t(`projecten.status.${m.status}`)}
                    </span>
                  </div>

                  <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
                    {cap != null && (
                      <div>
                        <dt className="text-gray-500">{t('projecten.hourCap')}</dt>
                        <dd className="font-medium text-gray-900">{cap} u</dd>
                        {extra > 0 && <dd className="text-xs text-orange-700">{t('projecten.extraGranted', { uren: extra })}</dd>}
                      </div>
                    )}
                    <div>
                      <dt className="text-gray-500">{t('projecten.budget')}</dt>
                      <dd className="font-medium text-gray-900">{euro(budget)}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">{t('projecten.hoursWritten')}</dt>
                      <dd className="font-medium text-gray-900">{geschreven.toFixed(2)}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">{t('projecten.rate')}</dt>
                      <dd className="font-medium text-gray-900">{tarief ? euro(tarief) : '—'}</dd>
                    </div>
                  </dl>

                  {(tarief || cap != null) && (
                    <div>
                      <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className={`h-full ${benut >= 1 ? 'bg-red-500' : benut >= 0.85 ? 'bg-orange-500' : 'bg-purple-500'}`}
                          style={{ width: `${Math.round(benut * 100)}%` }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {cap != null
                          ? `${t('projecten.hoursUsedOfCap', { gebruikt: geschreven.toFixed(2), cap })} · ${t('projecten.remainingHours', { uren: Math.max(0, cap - geschreven).toFixed(2) })}`
                          : t('projecten.budgetUsed', {
                              gebruikt: euro(geschreven * (tarief ?? 0)),
                              budget: euro(budget),
                            })}
                      </p>
                    </div>
                  )}

                  {m.status === 'CAP_REACHED_PENDING_APPROVAL' && (
                    <div className="mt-3 rounded-md bg-orange-50 border border-orange-200 p-3">
                      <p className="text-sm text-orange-900">{t('projecten.capReachedNotice')}</p>
                    </div>
                  )}
                  {m.status === 'LOCKED_PENDING' && <p className="mt-3 text-sm text-gray-600">{t('projecten.lockedPendingNotice')}</p>}
                  {m.status === 'COMPLETED_WAITING_FOR_FIAT' && (
                    <p className="mt-3 text-sm text-gray-600">{t('projecten.waitingForFiatNotice', { datum: fmtDatum(m.afgerond_op) })}</p>
                  )}
                  {m.status === 'ARCHIVED' && (
                    <p className="mt-3 text-sm text-gray-600">
                      {t('projecten.archivedNotice', { datum: fmtDatum(m.approved_at), bedrag: euro(geschreven * (tarief ?? 0)) })}
                    </p>
                  )}

                  {m.status === 'REJECTED' && m.afwijzing_reden && (
                    <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-3">
                      <p className="text-sm text-red-900">
                        <strong>{t('projecten.rejectedLabel')}:</strong> {m.afwijzing_reden}
                      </p>
                    </div>
                  )}

                  {m.status === 'SUBMITTED' && (
                    <p className="mt-3 text-sm text-gray-600">{t('projecten.lockedNotice')}</p>
                  )}

                  {magSchrijven(m.status) && (
                    <UrenFormulier
                      mijlpaal={m}
                      onKlaar={() => queryClient.invalidateQueries({ queryKey: ['professional-mijlpalen'] })}
                    />
                  )}

                  {magAfronden(m) && (
                    <AfrondKnop
                      mijlpaal={m}
                      onKlaar={() => queryClient.invalidateQueries({ queryKey: ['professional-mijlpalen'] })}
                    />
                  )}

                  {['PLANNED', 'REJECTED', 'SUBMITTED'].includes(m.status) && (
                    <UitbreidingFormulier
                      mijlpaal={m}
                      onKlaar={() => queryClient.invalidateQueries({ queryKey: ['professional-mijlpalen'] })}
                    />
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProfessionalProjectenPage;
